<div class="seg-info-container">
  <div class="seg-details">
    @for (field of headerFields; track trackByHeaderLabel($index, field)) {
      <div class="seg-field"
        [class.full-width]="field.fullWidth">
        <span class="field-label">{{ field.label | translate }}:</span>
        @if (field.value) {
          <span class="field-value">
            {{ field.translateValue ? (field.value | translate) : field.value }}
          </span>
        } @else {
          <span class="field-value empty">{{ 'N/A' | translate }}</span>
        }
      </div>
    }
  </div>
  @if (canEdit) {
    <div class="action-buttons">
      @if (!isEditing) {
        <button class="btn" (click)="enterEditMode()">{{'Edit' | translate}}</button>
      }
      @if (isEditing) {
        <button class="btn" [disabled]="isSaving" (click)="saveEdits()">{{'Save' | translate}}</button>
      }
      @if (isEditing) {
        <button class="btn btn-secondary" [disabled]="isSaving" (click)="cancelEdit()">{{'Cancel' | translate}}</button>
      }
    </div>
  }
</div>

<mat-tab-group [mat-stretch-tabs]="false" class="sidebar-tabs" [selectedIndex]="selectedTabIndex" (selectedTabChange)="onTabChange($event)">
  <mat-tab label="{{ 'Organization Level' | translate }}">
    <div class="custom-theme-table mt-3">
      <div class="custom-scrollbar table-responsive outer-bordered-table">
        <table mat-table class="table table-striped" [dataSource]="dataSource" matSort matSortDisableClear>

          <tr class="mat-row" *matNoDataRow [ngClass]="{ 'loading-row': isLoading }">
            <td class="mat-cell text-center" [attr.colspan]="displayedColumns.length">
              @if (isLoading) {
                <app-rings-loader class="bg-transparent"></app-rings-loader>
              } @else {
                {{ 'No data matching the filter' | translate }}
              }
            </td>
          </tr>

          <ng-container matColumnDef="OrgLevelName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'Organization Level' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              {{ row?.organizationLevelName ?? '-' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="shiftId">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'Shift' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              @if (isEditing) {
                <select class="edit-input" [class.pending-save]="isFieldPending(row, 'shiftId')"
                  [title]="isFieldPending(row, 'shiftId') ? ('Unsaved change' | translate) : ''"
                  [(ngModel)]="row.shiftId" (ngModelChange)="onShiftChange(row)">
                  <option [ngValue]="null">{{ 'None' | translate }}</option>
                  @for (shift of shifts; track trackByLookupId($index, shift)) {
                    <option [ngValue]="shift.id">
                      {{ shift.name }}
                    </option>
                  }
                </select>
              } @else {
                {{ row?.shiftName ?? '-' }}
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="shiftLengthHours">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'Shift Length (hrs)' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              @if (isEditing && !row.shiftId) {
                <input class="edit-input"
                  [class.pending-save]="isFieldPending(row, 'shiftLengthHours')"
                  [class.invalid-value]="row.shiftLengthHours | fieldOutOfRange: 'shiftLengthHours'"
                  [title]="isFieldPending(row, 'shiftLengthHours') ? ('Unsaved change' | translate) : ''"
                  type="number" step="0.5" min="0" max="24" [(ngModel)]="row.shiftLengthHours" />
              } @else {
                <span
                [class.pending-save]="isFieldPending(row, 'shiftLengthHours')">{{ row?.shiftLengthHours ?? '-' }}</span>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="avgWeeklyHours">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'Avg Weekly Hours' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              @if (isEditing && !row.shiftId) {
                <input class="edit-input"
                  [class.pending-save]="isFieldPending(row, 'avgWeeklyHours')"
                  [class.invalid-value]="row.avgWeeklyHours | fieldOutOfRange: 'avgWeeklyHours'"
                  [title]="isFieldPending(row, 'avgWeeklyHours') ? ('Unsaved change' | translate) : ''"
                  type="number" step="0.5" min="0" max="168" [(ngModel)]="row.avgWeeklyHours" />
              } @else {
                <span
                [class.pending-save]="isFieldPending(row, 'avgWeeklyHours')">{{ row?.avgWeeklyHours ?? '-' }}</span>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="shiftType">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'Shift Pattern' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              @if (isEditing && !row.shiftId) {
                <select class="edit-input"
                  [class.pending-save]="isFieldPending(row, 'shiftType')"
                  [title]="isFieldPending(row, 'shiftType') ? ('Unsaved change' | translate) : ''"
                  [(ngModel)]="row.shiftType" (ngModelChange)="onShiftTypeChange(row)">
                  <option [ngValue]="null">-</option>
                  @for (pattern of shiftPatternOptions; track trackByLookupId($index, pattern)) {
                    <option [ngValue]="pattern.id">
                      {{ pattern.name }}
                    </option>
                  }
                </select>
              } @else {
                <span
                [class.pending-save]="isFieldPending(row, 'shiftType')">{{ row?.shiftTypeLabel ?? '-' }}</span>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="numberOfWorkers">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'Number of Employees' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              @if (isEditing) {
                <input class="edit-input"
                  [class.pending-save]="isFieldPending(row, 'numberOfWorkers')"
                  [class.invalid-value]="row.numberOfWorkers | fieldOutOfRange: 'numberOfWorkers'"
                  [title]="isFieldPending(row, 'numberOfWorkers') ? ('Unsaved change' | translate) : ''"
                  type="number" step="1" min="0" max="1000000" [(ngModel)]="row.numberOfWorkers" />
              } @else {
                <span [class.zero-value]="row?.numberOfWorkers === 0">{{ row?.numberOfWorkers ?? '-' }}</span>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="numberOfContractors">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>
              {{ 'Number of Contractors' | translate }}
            </th>
            <td mat-cell *matCellDef="let row">
              @if (isEditing) {
                <input class="edit-input"
                  [class.pending-save]="isFieldPending(row, 'numberOfContractors')"
                  [class.invalid-value]="row.numberOfContractors | fieldOutOfRange: 'numberOfContractors'"
                  [title]="isFieldPending(row, 'numberOfContractors') ? ('Unsaved change' | translate) : ''"
                  type="number" step="1" min="0" max="1000000" [(ngModel)]="row.numberOfContractors" />
              } @else {
                <span [class.zero-value]="row?.numberOfContractors === 0">{{ row?.numberOfContractors ?? '-' }}</span>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let row" class="seg-remove-cell">
              @if (!row.isRemoved) {
                <button type="button" class="seg-remove-action" (click)="toggleRemoveRow(row)">
                  <span>{{ 'Remove' | translate }}</span>
                </button>
              } @else {
                <button type="button" class="seg-restore-action" (click)="toggleRemoveRow(row)">
                  <span>{{ 'Restore' | translate }}</span>
                </button>
              }
            </td>
          </ng-container>
          <ng-container matColumnDef="history">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let row" class="seg-history-cell">
              @if (row.id) {
                <span class="material-icons cursor-pointer material-grey-icon"
                  [matTooltip]="'View History' | translate"
                  (click)="openOrgLevelHistory(row)">history</span>
              }
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns" [class.seg-removed-row]="row.isRemoved"></tr>
        </table>
        @if (isEditing) {
          <div class="add-org-row">
            <app-org-tree-select
              [nodes]="orgPickerNodes"
              [selectedId]="null"
              [disabledIds]="disabledIds"
              [hideNullOption]="true"
              [placeholder]="'Add Organization Level'"
              (selectionChange)="onAddOrgLevel($event)">
            </app-org-tree-select>
          </div>
        }
      </div>
    </div>
  </mat-tab>

  <mat-tab label="{{ 'SEG History' | translate }}">
    <div class="custom-theme-table mt-3">
      <div class="custom-scrollbar table-responsive outer-bordered-table">
        <table mat-table class="table table-striped" [dataSource]="historyDataSource">
          <tr class="mat-row" *matNoDataRow [ngClass]="{ 'loading-row': isHistoryLoading }">
            <td class="mat-cell text-center" [attr.colspan]="historyDisplayedColumns.length">
              @if (isHistoryLoading) {
                <app-rings-loader class="bg-transparent"></app-rings-loader>
              } @else {
                {{ 'No data matching the filter' | translate }}
              }
            </td>
          </tr>

          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>{{ 'Name' | translate }}</th>
            <td mat-cell *matCellDef="let history" [ngClass]="{ 'highlight': $safeNavigationMigration(history.changedFieldSet?.has('Name')) }"><span>{{ history?.name || '-' }}</span></td>
          </ng-container>

          <ng-container matColumnDef="organizationLevelName">
            <th mat-header-cell *matHeaderCellDef>{{ 'Visibility' | translate }}</th>
            <td mat-cell *matCellDef="let history" [ngClass]="{ 'highlight': history.changedFieldSet?.has('OrganizationLevelName') || history.changedFieldSet?.has('OrganizationLevelId') }"><span>{{ history?.organizationLevelName || '-' }}</span></td>
          </ng-container>

          <ng-container matColumnDef="code">
            <th mat-header-cell *matHeaderCellDef>{{ 'Code' | translate }}</th>
            <td mat-cell *matCellDef="let history" [ngClass]="{ 'highlight': $safeNavigationMigration(history.changedFieldSet?.has('Code')) }"><span>{{ history?.code || '-' }}</span></td>
          </ng-container>

          <ng-container matColumnDef="reason">
            <th mat-header-cell *matHeaderCellDef>{{ 'Reason' | translate }}</th>
            <td mat-cell *matCellDef="let history" [ngClass]="{ 'highlight': $safeNavigationMigration(history.changedFieldSet?.has('Reason')) }"><span>{{ history?.reason || '-' }}</span></td>
          </ng-container>

          <ng-container matColumnDef="dateUpdated">
            <th mat-header-cell *matHeaderCellDef>{{ 'Updated Date' | translate }}</th>
            <td mat-cell *matCellDef="let history" [ngClass]="{ 'highlight': $safeNavigationMigration(history.changedFieldSet?.has('DateUpdated')) }"><span>{{ history.dateUpdatedDisplay }}</span></td>
          </ng-container>

          <ng-container matColumnDef="updatedByUserName">
            <th mat-header-cell *matHeaderCellDef>{{ 'Updated By' | translate }}</th>
            <td mat-cell *matCellDef="let history" [ngClass]="{ 'highlight': history.changedFieldSet?.has('UpdatedByUserName') || history.changedFieldSet?.has('UpdatedByUserId') }"><span>{{ history?.updatedByUserName || '-' }}</span></td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="historyDisplayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: historyDisplayedColumns"></tr>
        </table>
      </div>
    </div>
  </mat-tab>

  <mat-tab label="{{ 'Organization Level History' | translate }}">
    <ng-template matTabContent>
      <app-seg-org-level-history [segId]="data" [initialScopeEntityId]="orgLevelHistoryScopeId"></app-seg-org-level-history>
    </ng-template>
  </mat-tab>
</mat-tab-group>
